/**
 * Short-lived cache that lets the attendance dialog hand the stundenplan
 * tile-refresher post-save metadata (marked-rows count, roster size) without
 * threading another optional argument through 5 layers of props.
 *
 * Flow:
 * - `useAttendanceSave` calls {@link writeLessonSaveHints} right before
 *   firing `onSaved(lessonId)`.
 * - `useAttendancePermissions.updateLessonAttendanceStatus` reads + clears
 *   the entry via {@link consumeLessonSaveHints}, and if present takes the
 *   fast path (no `vw_react_lesson_details` / `lesson_diary_entries` /
 *   `student_attendance_logs` round-trips).
 *
 * Entries auto-expire after `MAX_AGE_MS` so a hint dropped on the floor
 * (e.g. dialog closes before parent re-renders) doesn't pollute later saves.
 */

import type { AttendanceSaveHints } from "../types/attendanceDialog";

const MAX_AGE_MS = 5_000;

const cache = new Map<string, { hints: AttendanceSaveHints; at: number }>();

export function writeLessonSaveHints(
  lessonId: string,
  hints: AttendanceSaveHints,
): void {
  cache.set(lessonId, { hints, at: Date.now() });
  // Opportunistic GC of stale entries.
  const cutoff = Date.now() - MAX_AGE_MS;
  for (const [k, v] of cache) {
    if (v.at < cutoff) cache.delete(k);
  }
}

export function consumeLessonSaveHints(
  lessonId: string,
): AttendanceSaveHints | undefined {
  const entry = cache.get(lessonId);
  if (!entry) return undefined;
  cache.delete(lessonId);
  if (Date.now() - entry.at > MAX_AGE_MS) return undefined;
  return entry.hints;
}
